import { ESdChangeReason, IScreenDesign, ITreeItem, SpecialDIIds } from "@kuika/kuika-cl-model";
import React from "react";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { IWithHistoryProp, withHistory } from "../../../../shared/hoc/with-history";
import { PureSdComponent } from "../../../../shared/pure-sd";
import { KMainFunctions } from "../../../../shared/utility/main-functions";
import KuikaDropdownMenu, { KuikaDropdownMenuItemType } from "../../../ui-kit/kuika-dropdown-menu/kuika-dropdown-menu";
import KuikaPopover from "../../../ui-kit/kuika-popover/kuika-popover";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import KuikaSmallMediumText from "../../../ui-kit/typography/SmallMediumText/SmallMediumText";
import { getTreeItemChildMenuItems, getTreeItemDisplayName, getTreeItemTestIdKey } from "./breadcrumb-menu-items";
import "./breadcrumb.scss";

// Row-2 breadcrumb collapses the middle of long paths by a CHARACTER budget (not a fixed
// node count): whole nodes are dropped from the middle — keeping the first node(s) and the
// last node(s), always incl. the selected — with a single "…", until the labels fit this
// available width. Full labels (incl. "(Type)") are preserved; nodes aren't cropped.
// The budget is DERIVED from the measured strip width (ResizeObserver) ÷ average glyph
// width; this fallback is only used before the first measurement.
const BREADCRUMB_CHAR_BUDGET_FALLBACK = 120;
const BREADCRUMB_AVG_CHAR_PX = 7; // ~13px medium font; pairs with getNodeCharLength's model

export interface IFooterBreadCrumbState {
  openMenus: { [key: string]: boolean };
  // Measured width of the breadcrumb strip (px); drives the character budget.
  availableWidth: number;
  // Whether the "…" hidden-components popover is open.
  isEllipsisOpen: boolean;
}

// A rendered breadcrumb node paired with its approximate character length and the
// source tree item, consumed by the character-budget collapse (the tree item is
// needed to list collapsed nodes under the "…").
interface IBreadcrumbNode {
  el: JSX.Element;
  len: number;
  node: ITreeItem;
}

export interface IFooterBreadCrumbProps {
  // In row-2 the screen selector already shows the project/screen, so skip the leading
  // project-name prefix and the screen-root node — render only the component path.
  hideProjectPrefix?: boolean;
}

class FooterBreadCrumb extends PureSdComponent<
  IFooterBreadCrumbProps & IWithSDProps & IWithHistoryProp,
  IFooterBreadCrumbState
> {
  private resizeObserver?: ResizeObserver;

  constructor(props: IFooterBreadCrumbProps & IWithSDProps & IWithHistoryProp) {
    super(props);
    this.state = { openMenus: {}, availableWidth: 0, isEllipsisOpen: false };
  }

  // Callback ref: the breadcrumb only mounts its container once a component is
  // selected, so attach the observer whenever that node appears (and detach on
  // unmount). A stable method reference keeps React from re-running it per render.
  private setContainer = (el: HTMLDivElement | null): void => {
    this.resizeObserver?.disconnect();
    this.resizeObserver = undefined;
    if (!el || typeof ResizeObserver === "undefined") return;
    const measure = (): void => {
      const width: number = el.clientWidth;
      if (width > 0 && width !== this.state.availableWidth) this.setState({ availableWidth: width });
    };
    this.resizeObserver = new ResizeObserver(measure);
    this.resizeObserver.observe(el);
    measure();
  };

  componentWillUnmount() {
    this.resizeObserver?.disconnect();
    if (this.ellipsisCloseTimer) clearTimeout(this.ellipsisCloseTimer);
  }

  setAsSelectedItem = (item: ITreeItem, isItScreen?: boolean) => {
    let id = item.kuikaId.toString();
    if (isItScreen) {
      id = "-1";
    }
    this.props.sd.setSelectedDisplayItem(id);
  };

  highlightHoveredItem = (item: any) => {
    this.props.sd.highlightHoveredItem(item.kuikaId.toString());
  };

  nodeTestIdKey = (x: ITreeItem): string => getTreeItemTestIdKey(x);

  getComponentName = (x: ITreeItem): string => getTreeItemDisplayName(this.props.sd, x);

  // Two-tone label: friendly name dark, "(type)" gray. Selected node inherits its blue.
  renderComponentName = (x: any, isSelected: boolean): React.ReactNode => {
    if (x.kuikaId && x.kuikaId.toString() === SpecialDIIds.rootDI.toString()) {
      return this.props.sd.sdName;
    }
    if (x.friendlyName) {
      return (
        <>
          <span className={isSelected ? undefined : "kuika-breadcrumb__name"}>{x.friendlyName}</span>
          <span className={isSelected ? undefined : "kuika-breadcrumb__type"}>{` (${x.name})`}</span>
        </>
      );
    }
    return x.name;
  };

  // Approximate the rendered character count of a node's label (+ separator),
  // used by the character-budget collapse below.
  getNodeCharLength = (x: any): number => {
    const SEPARATOR: number = 3; // " › " chevron + gaps, in char-equivalents
    if (x.kuikaId && x.kuikaId.toString() === SpecialDIIds.rootDI.toString()) {
      return (this.props.sd.sdName?.length ?? 0) + SEPARATOR;
    }
    if (x.friendlyName) {
      return x.friendlyName.length + (x.name?.length ?? 0) + 3 + SEPARATOR; // " (name)"
    }
    return (x.name?.length ?? 0) + SEPARATOR;
  };

  private ellipsisCloseTimer?: ReturnType<typeof setTimeout>;

  // Hover behaviour for the "…" popover: open on enter, close shortly after leave
  // (the delay bridges the gap between the trigger and the popover content).
  private openEllipsis = (): void => {
    if (this.ellipsisCloseTimer) clearTimeout(this.ellipsisCloseTimer);
    if (!this.state.isEllipsisOpen) this.setState({ isEllipsisOpen: true });
  };

  private scheduleCloseEllipsis = (): void => {
    if (this.ellipsisCloseTimer) clearTimeout(this.ellipsisCloseTimer);
    this.ellipsisCloseTimer = setTimeout(() => this.setState({ isEllipsisOpen: false }), 150);
  };

  private selectHiddenNode = (node: ITreeItem): void => {
    if (this.ellipsisCloseTimer) clearTimeout(this.ellipsisCloseTimer);
    this.highlightHoveredItem(node);
    this.setAsSelectedItem(node);
    this.setState({ isEllipsisOpen: false });
  };

  // The "…" opens a popover listing the collapsed (hidden) middle nodes so they
  // stay reachable; clicking one selects that component. Uses KuikaPopover (Radix,
  // portaled + reliable) rather than the inline dropdown.
  renderEllipsisNode = (dropped: ITreeItem[]): JSX.Element => (
    <div className="kuika-breadcrumb__item" key="breadcrumb-ellipsis">
      <KuikaPopover
        visible={this.state.isEllipsisOpen}
        onVisibleChange={(v) => this.setState({ isEllipsisOpen: v })}
        position="bottom"
        align="start"
        sideOffset={6}
        hideCloseIcon
        title="Hidden components"
        className="kuika-breadcrumb__hidden-pop"
        // Hover-alive on the whole popover surface (title + padding included), so
        // moving down from the "…" doesn't die on the title bar before reaching the list.
        onMouseEnter={this.openEllipsis}
        onMouseLeave={this.scheduleCloseEllipsis}
        content={
          <div className="kuika-breadcrumb__hidden-list">
            {dropped.map((node) => (
              <div
                key={node.kuikaId.toString()}
                className="kuika-breadcrumb__hidden-item"
                role="button"
                tabIndex={0}
                onClick={() => this.selectHiddenNode(node)}
              >
                <KuikaSmallMediumText>{this.getComponentName(node)}</KuikaSmallMediumText>
              </div>
            ))}
          </div>
        }
      >
        <span
          className="kuika-breadcrumb__item-content"
          role="button"
          tabIndex={0}
          onMouseEnter={this.openEllipsis}
          onMouseLeave={this.scheduleCloseEllipsis}
        >
          <KuikaSmallMediumText>…</KuikaSmallMediumText>
          <KuikaSvg name="chevron-right" type="arrows" width={12} height={12} />
        </span>
      </KuikaPopover>
    </div>
  );

  // Character-budget collapse: keep the last (selected) node, then greedily keep
  // nodes from the end and always at least the first node, dropping the middle
  // with a single "…" so the path fits the row-2 breadcrumb strip. Full labels
  // are preserved (no per-node truncation) — whole nodes are dropped instead.
  collapseByBudget = (items: IBreadcrumbNode[], charBudget: number): React.ReactNode[] => {
    const n: number = items.length;
    if (n <= 2) return items.map((x) => x.el);
    const total: number = items.reduce((sum, x) => sum + x.len, 0);
    if (total <= charBudget) return items.map((x) => x.el);

    const ELLIPSIS_LEN: number = 3;
    let budget: number = charBudget - ELLIPSIS_LEN;
    const reserveFirst: number = items[0].len;

    // Suffix: the selected (last) node always, then more from the end while there's
    // room — leaving reserveFirst so the first node can still be shown.
    const keepEnd: number[] = [n - 1];
    budget -= items[n - 1].len;
    let i: number = n - 2;
    while (i >= 1 && items[i].len <= budget - reserveFirst) {
      keepEnd.push(i);
      budget -= items[i].len;
      i--;
    }

    // Prefix: from the start up to the suffix boundary.
    const keepStart: number[] = [];
    let j: number = 0;
    while (j <= i && items[j].len <= budget) {
      keepStart.push(j);
      budget -= items[j].len;
      j++;
    }
    if (keepStart.length === 0) keepStart.push(0); // always show at least the first node

    keepEnd.reverse(); // ascending order
    const keptIndexes: Set<number> = new Set<number>([...keepStart, ...keepEnd]);
    const droppedNodes: ITreeItem[] = items.filter((_, idx) => !keptIndexes.has(idx)).map((it) => it.node);
    const out: React.ReactNode[] = [];
    keepStart.forEach((idx) => out.push(items[idx].el));
    if (droppedNodes.length > 0) out.push(this.renderEllipsisNode(droppedNodes));
    keepEnd.forEach((idx) => out.push(items[idx].el));
    return out;
  };

  getCurrentScreen = () => {
    const sdId = KMainFunctions.getSdIdFromUrl();
    const currentScreen = this.props.sd?.screenList?.find((s) => s.id === sdId);
    return currentScreen;
  };

  getMenuItems = (x: ITreeItem): KuikaDropdownMenuItemType[] => getTreeItemChildMenuItems(this.props.sd, x);

  handleOpenChange = (open: boolean, itemId: string) => {
    this.setState((prevState) => ({
      openMenus: {
        ...prevState.openMenus,
        [itemId]: open
      }
    }));
  };

  renderComponentTree = (x: ITreeItem, i: number, kuikaId: string, tree: ITreeItem[]) => {
    const isSelected: boolean = x.kuikaId.toString() === kuikaId.toString();
    const testIdKey: string = this.nodeTestIdKey(x);
    return (
      <div
        className={`kuika-breadcrumb__item${isSelected ? " kuika-breadcrumb__item--selected" : ""}`}
        key={`tree_${i}`}
      >
        <KuikaDropdownMenu
          options={this.getMenuItems(x)}
          open={this.state.openMenus[x.kuikaId] || false}
          setOpen={(open) => {
            if (x.children && x.children.length > 0) {
              this.handleOpenChange(open, x.kuikaId);
            }
          }}
          trigger="hover"
          placement="topCenter"
          overlayClassName="kuika_breadcrumb_dropdown"
          menuTestId={`breadcrumbDropdown-${testIdKey}`}
          header={x.children && x.children.length > 0 ? this.getComponentName(x) : undefined}
        >
          {/* Two ids on purpose. The dropdown trigger is this wrapper (KuikaDropdownMenu wraps
              it, trigger="hover"), while the click that SELECTS the node lives on the text
              below — so the wrapper's padding is inert to a click, the same trap the item tree
              and the table column chooser already carry. Hover the segment, click the label. */}
          <div className="kuika-breadcrumb__item-content" data-testid={`breadcrumbSegment-${testIdKey}`}>
            <KuikaSmallMediumText
              data-testid={`breadcrumbSegmentLabel-${testIdKey}`}
              style={x.kuikaId.toString() === kuikaId.toString() ? { color: "#246eff" } : {}}
              onClick={(e) => this.setAsSelectedItem(x)}
              onMouseEnter={(e) => this.highlightHoveredItem(x)}
            >
              {this.renderComponentName(x, x.kuikaId.toString() === kuikaId.toString())}
            </KuikaSmallMediumText>
            {i !== tree.length - 1 && <KuikaSvg name="chevron-right" type="arrows" width={12} height={12} />}
          </div>
        </KuikaDropdownMenu>
      </div>
    );
  };

  renderLayouts = (sd: IScreenDesign, kuikaId: string, tree: ITreeItem[], currScreen, x: ITreeItem, i, id: number) => {
    const isSelected: boolean = x.kuikaId.toString() === kuikaId.toString();
    const testIdKey: string = this.nodeTestIdKey(x);
    return (
      <div
        className={`kuika-breadcrumb__item${isSelected ? " kuika-breadcrumb__item--selected" : ""}`}
        onMouseEnter={(e) => this.highlightHoveredItem(x)}
        key={`tree_${i}`}
      >
        <KuikaDropdownMenu
          options={this.getMenuItems(x)}
          open={this.state.openMenus[x.kuikaId] || false}
          setOpen={(open) => {
            if (x.children && x.children.length > 0) {
              this.handleOpenChange(open, x.kuikaId);
            }
          }}
          trigger="hover"
          placement="topCenter"
          overlayClassName="kuika_breadcrumb_dropdown"
          menuTestId={`breadcrumbDropdown-${testIdKey}`}
          header={x.children && x.children.length > 0 ? this.getComponentName(x) : undefined}
        >
          {/* Same two-id split as renderComponentTree — see the note there. */}
          <div className="kuika-breadcrumb__item-content" data-testid={`breadcrumbSegment-${testIdKey}`}>
            <KuikaSmallMediumText
              data-testid={`breadcrumbSegmentLabel-${testIdKey}`}
              style={x.kuikaId.toString() === kuikaId.toString() ? { color: "#246eff" } : {}}
              onMouseEnter={(e) => this.highlightHoveredItem(x)}
              onClick={(e) => this.setAsSelectedItem(x)}
            >
              {this.renderComponentName(x, x.kuikaId.toString() === kuikaId.toString())}
            </KuikaSmallMediumText>
            {i !== tree.length - 1 && <KuikaSvg name="chevron-right" type="arrows" width={12} height={12} />}
          </div>
        </KuikaDropdownMenu>
      </div>
    );
  };

  render() {
    const isReportDesignerLandingVisible: boolean = this.props.sd.isReportDesignerLandingVisible();
    const activeKuikaComponent = this.props.sd.getActiveKuikaComponent();
    if (!activeKuikaComponent.tree || isReportDesignerLandingVisible) {
      return <></>;
    }
    const { tree } = activeKuikaComponent;
    const { kuikaId } = activeKuikaComponent;
    const sd = this.props.sd.getScreenDesign();
    const currScreen = this.getCurrentScreen();

    const renderLayoutsFor = (x: ITreeItem, i: number): JSX.Element | null => {
      switch (x.name) {
        case "Header":
          return this.renderLayouts(sd, kuikaId, tree, currScreen, x, i, SpecialDIIds.headerDI);
        case "Footer":
          return this.renderLayouts(sd, kuikaId, tree, currScreen, x, i, SpecialDIIds.footerDI);
        case "Page Header":
          return this.renderLayouts(sd, kuikaId, tree, currScreen, x, i, SpecialDIIds.pageHeaderDI);
        case "Page Footer":
          return this.renderLayouts(sd, kuikaId, tree, currScreen, x, i, SpecialDIIds.pageFooterDI);
        case "LayoutHeader":
          return this.renderLayouts(sd, kuikaId, tree, currScreen, x, i, SpecialDIIds.layoutHeaderDI);
        case "LayoutFooter":
          return this.renderLayouts(sd, kuikaId, tree, currScreen, x, i, SpecialDIIds.layoutFooterDI);
        case "LayoutLeft":
          return this.renderLayouts(sd, kuikaId, tree, currScreen, x, i, SpecialDIIds.layoutLeftDI);
        case "LayoutRight":
          return this.renderLayouts(sd, kuikaId, tree, currScreen, x, i, SpecialDIIds.layoutRightDI);
        default:
          return null;
      }
    };

    const items: IBreadcrumbNode[] = tree
      .map((x, i): IBreadcrumbNode | null => {
        // In row-2 the screen-root node is already represented by the screen selector.
        if (this.props.hideProjectPrefix && x.kuikaId?.toString() === SpecialDIIds.rootDI.toString()) {
          return null;
        }
        const layoutEl: JSX.Element | null = currScreen ? renderLayoutsFor(x, i) : null;
        const el: JSX.Element = layoutEl ?? this.renderComponentTree(x, i, kuikaId, tree);
        return { el, len: this.getNodeCharLength(x), node: x };
      })
      .filter((it): it is IBreadcrumbNode => it != null);

    // Row-2 collapses the middle by a width-derived character budget; elsewhere show all.
    const charBudget: number =
      this.state.availableWidth > 0
        ? Math.floor(this.state.availableWidth / BREADCRUMB_AVG_CHAR_PX)
        : BREADCRUMB_CHAR_BUDGET_FALLBACK;
    const displayNodes: React.ReactNode[] = this.props.hideProjectPrefix
      ? this.collapseByBudget(items, charBudget)
      : items.map((it) => it.el);

    return (
      <div className="kuika-breadcrumb" ref={this.setContainer} data-testid="designBreadcrumb">
        {!this.props.hideProjectPrefix && (
          <div className="kuika-breadcrumb__item">
            <KuikaSmallMediumText>{this.props?.sd?.project?.name}</KuikaSmallMediumText>
            <KuikaSvg name="chevron-right" type="arrows" width={12} height={12} />
          </div>
        )}
        {/* In row-2, lead with a ">" so it reads "HomePage > <path>" after the screen selector. */}
        {this.props.hideProjectPrefix && items.length > 0 && (
          <KuikaSvg name="chevron-right" type="arrows" width={12} height={12} />
        )}
        {displayNodes}
      </div>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    if (
      changeReason === ESdChangeReason.ChangeDiFriendlyName ||
      changeReason === ESdChangeReason.ChangeSelectedDiByCanvasFrame ||
      changeReason === ESdChangeReason.ChangeSelectedDiByTools ||
      changeReason === ESdChangeReason.DragDropDi ||
      changeReason === ESdChangeReason.DragDropBlock
    ) {
      return true;
    }
    return false;
  };
}

const footerBreadCrumb = withHistory<IFooterBreadCrumbProps>(withSD<IFooterBreadCrumbProps>(FooterBreadCrumb));
export { footerBreadCrumb as FooterBreadCrumb };
